import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Monitor, Smartphone, Undo2, Redo2, Eye, Pencil, Check, Image as ImageIcon } from "lucide-react";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import presetNewsletterImport from "grapesjs-preset-newsletter";
import "./EmailBuilder.css";
import { normaliseSeedHtml, TEXT_DIV_OPEN } from "../utils/emailSeedHtml";

// Normalise the plugin export across bundler interop shapes (fn vs {default}).
const presetNewsletter = presetNewsletterImport?.default || presetNewsletterImport;

/**
 * Mailchimp-style drag-and-drop email builder, powered by GrapesJS + the
 * newsletter preset (self-hosted, no external calls). It is UNCONTROLLED after
 * mount: the GrapesJS editor becomes the source of truth, seeded once from the
 * `design` (builder JSON) or, failing that, from `html` (raw HTML). To load a
 * different starting point later (e.g. "start from template"), remount by
 * changing the component `key` in the parent.
 *
 * On every change it emits BOTH:
 *   - design_json: editor.getProjectData() so the design can be re-opened/edited
 *   - html_body:  a full CSS-inlined email document (passes straight through the
 *                 send engine, which forwards any body containing <!doctype>/<html>)
 *
 * Brand values mirror core/services/email_templates.py so the builder output
 * matches real Collings sends.
 */

const BRAND = "#2DC8DB";
const BRAND_DARK = "#25AFC0";
const LOGO_URL = "https://crm.collings.com.au/collingslogo-removebg-preview.png";
const WEBSITE_URL = "https://www.collings.com.au";
const SUPPORT_EMAIL = "info@collings.com.au";
// The literal token the send engine swaps for the real per-recipient link.
const UNSUB_TOKEN = "%%UNSUBSCRIBE_URL%%";

// Merge tags the backend (comms_engine.build_merge_context) substitutes.
const MERGE_TAGS = [
  { tag: "{{FirstName}}", label: "First name" },
  { tag: "{{Name}}", label: "Full name" },
  { tag: "{{Suburb}}", label: "Suburb" },
  { tag: "{{State}}", label: "State" },
  { tag: "{{Email}}", label: "Email" },
  { tag: "{{Company}}", label: "Company" },
];

/** Wrap the inlined body HTML into a complete, email-client-safe document with a
 *  hidden preheader. A full <!doctype>/<html> document is forwarded untouched by
 *  the send engine (_ensure_branded_html), so the builder owns the whole email. */
function wrapEmailDocument(innerHtml, preheader) {
  const body = (innerHtml || "").trim();
  // If the inliner already returned a full document, leave it be.
  if (/^<!doctype/i.test(body) || /^<html[\s>]/i.test(body)) return body;
  const pre = (preheader || "").trim();
  const preheaderHtml = pre
    ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;">${pre}</div>`
    : "";
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
</head>
<body style="margin:0;padding:0;background:#f4f6f8;">
${preheaderHtml}
${body}
</body>
</html>`;
}

// Inline SVG icons for the custom block cards (stroke uses currentColor so they
// pick up the teal hover). Kept as strings because GrapesJS `media` wants markup.
const svg = (inner) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
const ICONS = {
  image: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>'),
  button: svg('<rect x="3" y="8" width="18" height="8" rx="4"/><path d="M8 12h8"/>'),
  home: svg('<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20h14V9.5"/>'),
  user: svg('<circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>'),
  footer: svg('<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 15h18"/>'),
  pin: svg('<path d="M12 21s7-6.5 7-11a7 7 0 0 0-14 0c0 4.5 7 11 7 11Z"/><circle cx="12" cy="10" r="2.5"/>'),
  mail: svg('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>'),
  building: svg('<rect x="5" y="3" width="14" height="18" rx="1"/><path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01"/>'),
  text: svg('<path d="M5 5h14"/><path d="M12 5v14"/><path d="M9 19h6"/>'),
  link: svg('<path d="M10 13a4 4 0 0 0 5.66 0l2.83-2.83a4 4 0 0 0-5.66-5.66L11.5 5.85"/><path d="M14 11a4 4 0 0 0-5.66 0L5.5 13.83a4 4 0 1 0 5.66 5.66L12.5 18.1"/>'),
};
const MERGE_ICON = {
  "{{FirstName}}": ICONS.user,
  "{{Name}}": ICONS.user,
  "{{Suburb}}": ICONS.pin,
  "{{State}}": ICONS.pin,
  "{{Email}}": ICONS.mail,
  "{{Company}}": ICONS.building,
};

/**
 * Elementor-style Style panel: a full, organised set of controls applied to
 * whichever block is selected. `buildProps` pulls GrapesJS's built-in widgets
 * (font-family select, color pickers, composite padding/margin with 4 sides,
 * border builder, box-shadow stack, text-align icon radios, etc.) so each block
 * gets proper, applicable styling rather than the preset's sparse defaults.
 * Only sectors relevant to the selection show non-empty controls.
 */
const STYLE_SECTORS = [
  {
    name: "Typography",
    open: true,
    buildProps: [
      "font-family",
      "font-size",
      "font-weight",
      "letter-spacing",
      "color",
      "line-height",
      "text-align",
      "text-decoration",
      "font-style",
      "text-transform",
    ],
  },
  {
    name: "Dimensions",
    open: false,
    buildProps: ["width", "max-width", "height", "min-height"],
  },
  {
    name: "Spacing",
    open: false,
    buildProps: ["padding", "margin"],
  },
  {
    name: "Background",
    open: false,
    buildProps: [
      "background-color",
      "background-image",
      "background-repeat",
      "background-position",
      "background-size",
    ],
  },
  {
    name: "Border & radius",
    open: false,
    buildProps: ["border-radius", "border"],
  },
  {
    name: "Effects",
    open: false,
    buildProps: ["opacity", "box-shadow"],
  },
];

// Which CSS properties are appropriate to style, per kind of block. Feeding
// these as each component type's `stylable` list makes the Style panel
// contextual (Elementor-style): a text/button block shows Typography, an image
// shows sizing/border/effects but NOT font controls, a container shows
// layout/background/border. Property ids match STYLE_SECTORS' buildProps.
const TYPO_PROPS = [
  "font-family", "font-size", "font-weight", "letter-spacing", "color",
  "line-height", "text-align", "text-decoration", "font-style", "text-transform",
];
const BOX_PROPS = ["width", "max-width", "height", "min-height", "padding", "margin"];
const DECO_PROPS = [
  "background-color", "background-image", "background-repeat",
  "background-position", "background-size", "border", "border-radius",
  "opacity", "box-shadow",
];

// Settings for a link/button. GrapesJS's own defaults are "Href" / "Target",
// which nobody hunting for "the link" on a button recognises (Paul Jimoulis,
// CRM-2026-00047). The address and the new-tab choice are handled by the
// "Where this button or link goes" box at the top of the Settings rail, which
// also works when the click landed on the cell or table around the button - so
// they are dropped from here rather than renamed, leaving ONE place to set the
// destination instead of two boxes holding the same URL.
const LINK_TRAITS = [{ name: "title", label: "Tooltip (optional)" }];

/** Restrict each component type's stylable properties so the Style panel only
 *  offers controls that make sense for the selected block. */
function applyContextualStyling(editor) {
  const dc = editor.DomComponents;
  const ext = (type, list, defaults = {}) => {
    if (!dc.getType(type)) return;
    dc.addType(type, { model: { defaults: { stylable: list, ...defaults } } });
  };

  // Text and links/buttons: full typography + spacing/sizing + decoration.
  ext("text", [...TYPO_PROPS, ...BOX_PROPS, ...DECO_PROPS]);
  ext("link", [...TYPO_PROPS, ...BOX_PROPS, ...DECO_PROPS], { traits: LINK_TRAITS });
  ext("textnode", TYPO_PROPS);

  // Images: sizing, spacing, border/radius, effects - no typography.
  ext("image", [
    "width", "max-width", "height", "min-height", "padding", "margin",
    "border", "border-radius", "opacity", "box-shadow", "background-color",
  ]);

  // Structural blocks (tables / rows / cells / generic wrappers): layout,
  // background, border and alignment - but not font styling.
  ["cell", "table", "row", "tbody", "thead", "tfoot", "default"].forEach((t) =>
    ext(t, [...BOX_PROPS, "text-align", ...DECO_PROPS])
  );
}

/** Register Collings-branded blocks on top of the newsletter preset's defaults. */
function addCollingsBlocks(editor) {
  const bm = editor.BlockManager;
  const category = "Collings";

  // Text first, because "add some words of my own" is the most common thing an
  // agent wants and the preset buried its own Text tile in an unnamed group at
  // the bottom of the rail (Joshua Diokno, CRM-2026-00047: "a user can freely
  // compose and manually add texts... Apparently this is not the case").
  // `activate: true` drops it straight into typing - no hunt for the
  // double-click. The preset's duplicate is removed so there is one Text tile.
  bm.remove("text");
  bm.add("collings-text", {
    label: "Text",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.text,
    activate: true,
    content: `${TEXT_DIV_OPEN}Type your message here.</div>`,
  });

  bm.add("collings-logo-header", {
    label: "Logo header",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.image,
    content: `<table style="width:100%;border-collapse:collapse;background:#ffffff;">
      <tr><td style="padding:24px;text-align:center;">
        <img src="${LOGO_URL}" alt="Collings Real Estate" style="height:40px;max-width:220px;" />
      </td></tr>
    </table>`,
  });

  // The preset ships two link tiles and neither works for an email: "Link" is a
  // bare inline <a>, so it is dropped INTO whatever sentence the pointer is
  // nearest and then cannot be dragged back out, and "Link Block" is an empty
  // 50x50 box that looks broken. Replaced with one block-level link that lands
  // on its own line (Joshua Diokno, CRM-2026-00047: "it lands arbitrarily on any
  // line of text; moving it around also feels limited").
  bm.remove("link");
  bm.remove("link-block");
  bm.add("collings-link", {
    label: "Text link",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.link,
    // Selected on drop, so the "Where this button or link goes" box is already
    // open and the address can be pasted without hunting for it.
    activate: true,
    content: `<table style="width:100%;border-collapse:collapse;">
      <tr><td style="padding:10px 0;font-family:Arial,sans-serif;font-size:15px;">
        <a href="${WEBSITE_URL}" style="color:${BRAND_DARK};text-decoration:underline;">View the listing</a>
      </td></tr>
    </table>`,
  });

  bm.add("collings-cta", {
    label: "Button (CTA)",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.button,
    activate: true,
    content: `<table style="border-collapse:collapse;margin:16px auto;">
      <tr><td style="border-radius:8px;background:${BRAND};padding:12px 26px;text-align:center;">
        <a href="${WEBSITE_URL}" style="color:#ffffff;font-family:Arial,sans-serif;font-size:15px;font-weight:bold;text-decoration:none;">Enquire now</a>
      </td></tr>
    </table>`,
  });

  bm.add("collings-property-card", {
    label: "Property card",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.home,
    content: `<table style="width:100%;max-width:560px;margin:0 auto;border-collapse:collapse;border:1px solid #e5e9ec;border-radius:10px;overflow:hidden;">
      <tr><td>
        <img src="https://via.placeholder.com/560x300?text=Property+photo" alt="Property" style="width:100%;display:block;" />
      </td></tr>
      <tr><td style="padding:16px;font-family:Arial,sans-serif;">
        <div style="font-size:18px;font-weight:bold;color:#1f2933;">14 Example Street, Suburb</div>
        <div style="font-size:14px;color:#556;margin-top:4px;">3 bed &nbsp;2 bath &nbsp;1 car</div>
        <div style="font-size:16px;font-weight:bold;color:${BRAND_DARK};margin-top:8px;">$850,000</div>
      </td></tr>
    </table>`,
  });

  bm.add("collings-agent-signature", {
    label: "Agent signature",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.user,
    content: `<table style="width:100%;max-width:560px;margin:16px auto;border-collapse:collapse;border-top:1px solid #e5e9ec;">
      <tr><td style="padding:16px 4px;font-family:Arial,sans-serif;">
        <div style="font-size:15px;font-weight:bold;color:#1f2933;">{{Name}}</div>
        <div style="font-size:13px;color:#556;">Collings Real Estate</div>
        <div style="font-size:13px;color:${BRAND_DARK};">${SUPPORT_EMAIL}</div>
      </td></tr>
    </table>`,
  });

  bm.add("collings-footer", {
    label: "Footer + unsubscribe",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.footer,
    content: `<table style="width:100%;border-collapse:collapse;background:#f4f6f8;">
      <tr><td style="padding:22px 16px;text-align:center;font-family:Arial,sans-serif;font-size:12px;color:#8a97a3;">
        <div>Collings Real Estate</div>
        <div style="margin-top:6px;">
          <a href="mailto:${SUPPORT_EMAIL}" style="color:${BRAND_DARK};text-decoration:none;">${SUPPORT_EMAIL}</a>
          &nbsp;·&nbsp;
          <a href="${WEBSITE_URL}" style="color:${BRAND_DARK};text-decoration:none;">collings.com.au</a>
        </div>
        <div style="margin-top:10px;">
          You're receiving this because you're a Collings contact.
          <a href="${UNSUB_TOKEN}" style="color:${BRAND_DARK};text-decoration:underline;">Unsubscribe</a>
        </div>
      </td></tr>
    </table>`,
  });

  // Placed right after Footer (not left to the newsletter preset's own
  // uncategorised "Image" block, which renders at the very bottom of the
  // whole panel below Merge tags) so it lands in the Collings grid's last,
  // otherwise-empty slot next to Footer + unsubscribe (CG, 2026-08-28).
  // Removes the preset's own "image" block so there's one Image tile, not two.
  bm.remove("image");
  bm.add("collings-image", {
    label: "Image",
    category,
    attributes: { class: "ceb-cico" },
    media: ICONS.image,
    content: `<table style="width:100%;max-width:560px;margin:0 auto;border-collapse:collapse;">
      <tr><td style="padding:0;">
        <img src="https://via.placeholder.com/560x300?text=Image" alt="Image" style="width:100%;display:block;" />
      </td></tr>
    </table>`,
  });

  // Merge-tag chips: drop a personalisation token straight into the canvas.
  MERGE_TAGS.forEach(({ tag, label }) => {
    bm.add(`merge-${tag}`, {
      label,
      category: "Merge tags",
      attributes: { class: "ceb-cico" },
      media: MERGE_ICON[tag] || ICONS.user,
      content: `<span>${tag}</span>`,
    });
  });
}

/** Is this component an anchor - i.e. the thing that carries the link? */
function isLinkComp(c) {
  if (!c) return false;
  try {
    if (c.is && c.is("link")) return true;
  } catch (e) {
    /* noop */
  }
  const tag = (c.get && c.get("tagName")) || "";
  return String(tag).toLowerCase() === "a";
}

/**
 * The <a> an agent means when they click a button in the canvas. A bulletproof
 * email button is a table > tr > td wrapping the anchor, so a click can land on
 * any of four components; and text inside a link selects the text node. So we
 * look at the selection itself, then the links it sits inside, then the single
 * link inside it. Several links inside (a footer row, say) is genuinely
 * ambiguous, so we say so instead of guessing.
 */
function resolveLink(comp) {
  if (!comp) return { link: null, ambiguous: false };
  if (isLinkComp(comp)) return { link: comp, ambiguous: false };

  let parent = comp.parent && comp.parent();
  for (let i = 0; parent && i < 10; i += 1) {
    if (isLinkComp(parent)) return { link: parent, ambiguous: false };
    parent = parent.parent && parent.parent();
  }

  const found = [];
  const walk = (m, depth) => {
    if (!m || depth > 8) return;
    const kids = m.components && m.components();
    if (!kids || !kids.forEach) return;
    kids.forEach((k) => {
      if (isLinkComp(k)) found.push(k);
      else walk(k, depth + 1);
    });
  };
  walk(comp, 0);
  if (found.length === 1) return { link: found[0], ambiguous: false };
  return { link: null, ambiguous: found.length > 1 };
}

/**
 * The first link inside `comp` that has no address yet. GrapesJS's text toolbar
 * writes `<a href="">the selected words</a>`, which looks like a link and goes
 * nowhere until someone fills it in - so we hunt for it and open the address box
 * on it rather than leaving the agent to find it.
 */
function findBlankLink(comp, depth = 0) {
  if (!comp || depth > 8) return null;
  if (isLinkComp(comp)) {
    let attrs = {};
    try {
      attrs = (comp.getAttributes && comp.getAttributes()) || {};
    } catch (e) {
      attrs = {};
    }
    const href = (attrs.href || "").trim();
    if (!href || href === "#") return comp;
  }
  const kids = comp.components && comp.components();
  if (!kids || !kids.map) return null;
  let hit = null;
  kids.forEach((k) => {
    if (!hit) hit = findBlankLink(k, depth + 1);
  });
  return hit;
}

/** The visible text of a link, for spotting the "or paste this link into your
 *  browser" line - one whose words ARE the address it points at. */
function linkPrintsItsUrl(link, href) {
  let text = "";
  try {
    const el = link.getEl && link.getEl();
    text = ((el && el.textContent) || "").trim();
  } catch (e) {
    return false;
  }
  if (!text) return false;
  return /^https?:\/\/\S+$/i.test(text) || (!!href && text === href);
}

export default function EmailBuilder({ design, html, preheader, onChange, onClose, title = "Design email" }) {
  const canvasRef = useRef(null);
  const blocksRef = useRef(null);
  const styleRef = useRef(null);
  const settingsRef = useRef(null);
  const selectorsRef = useRef(null);
  const layersRef = useRef(null);
  const editorRef = useRef(null);
  const preheaderRef = useRef(preheader);
  const onChangeRef = useRef(onChange);
  // The <a> behind the current selection, and a force-export hook, so the
  // link editor below can write a destination and have it saved straight away.
  const linkCompRef = useRef(null);
  const emitRef = useRef(null);
  // Export everything now, including words still being typed. Used by Done.
  const flushRef = useRef(null);
  const linkTimerRef = useRef(null);
  const linkDoneTimerRef = useRef(null);

  const [device, setDevice] = useState("Desktop");
  const [rightTab, setRightTab] = useState("settings");
  const [previewing, setPreviewing] = useState(false);
  // Whether the currently-selected element is an image, so we can offer a
  // one-click "Replace image" in the Settings rail.
  const [imageSelected, setImageSelected] = useState(false);
  // The link editor's state: whether the selection has a link behind it, the
  // address in the box, and whether it opens in a new tab.
  const [linkState, setLinkState] = useState({ has: false, ambiguous: false, printsUrl: false });
  const [linkDraft, setLinkDraft] = useState("");
  const [linkNewTab, setLinkNewTab] = useState(false);
  // Briefly true right after "Apply link" is pressed, so the button can say so
  // before the box closes itself as the confirmation (Michael Piccoli,
  // CRM-2026-00047 - "nothing happens to tell you that it has" saved).
  const [linkSaved, setLinkSaved] = useState(false);

  // Keep the latest preheader/onChange available to the (long-lived) editor
  // listeners without re-initialising the editor.
  useEffect(() => {
    preheaderRef.current = preheader;
  }, [preheader]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!canvasRef.current || editorRef.current) return;

    // Custom, Mailchimp-style layout: we strip GrapesJS's built-in dark panels
    // (panels.defaults: []) and mount each manager into our own light-themed
    // rails - blocks on the left, style/settings/layers on the right.
    const editor = grapesjs.init({
      container: canvasRef.current,
      height: "100%",
      width: "auto",
      fromElement: false,
      storageManager: false,
      undoManager: true,
      panels: { defaults: [] },
      blockManager: { appendTo: blocksRef.current },
      styleManager: { appendTo: styleRef.current, sectors: STYLE_SECTORS, clearProperties: true },
      traitManager: { appendTo: settingsRef.current },
      selectorManager: { appendTo: selectorsRef.current, componentFirst: true },
      layerManager: { appendTo: layersRef.current },
      // Asset manager: drop/browse a file (embedded inline; the send engine
      // re-hosts inline images to S3) or paste an image URL. Selecting an asset
      // while an image is targeted REPLACES that image in place - so you never
      // have to delete and re-add a picture to swap it.
      assetManager: {
        embedAsBase64: true,
        dropzone: true,
        openAssetsOnDrop: true,
        showUrlInput: true,
        autoAdd: true,
      },
      deviceManager: {
        devices: [
          { name: "Desktop", width: "" },
          { name: "Mobile", width: "375px", widthMedia: "480px" },
        ],
      },
      plugins: [presetNewsletter],
      pluginsOpts: {
        [presetNewsletter]: {
          inlineCss: true,
          showBlocksOnLoad: false,
          modalLabelImport: "Paste your HTML here and click Import",
        },
      },
    });
    editorRef.current = editor;
    addCollingsBlocks(editor);
    // Make the Style panel contextual per block type. Done before seeding so
    // components created from the seeded HTML pick up the right stylable lists.
    applyContextualStyling(editor);

    // Surface the selected element's options automatically, and detect when an
    // image is selected so React can show the "Replace image" control.
    const isImageComp = (c) => {
      if (!c) return false;
      if (c.is && c.is("image")) return true;
      const tag = (c.get && c.get("tagName")) || "";
      return tag.toLowerCase() === "img";
    };
    // Pick up the link behind the selection so the Settings rail can offer a
    // plain "Link address" box - clicking a button lands on the table, the row,
    // the cell or the text as often as it lands on the anchor itself.
    const readLink = (c) => {
      const { link, ambiguous } = resolveLink(c);
      linkCompRef.current = link;
      clearTimeout(linkDoneTimerRef.current);
      setLinkSaved(false);
      if (!link) {
        setLinkState({ has: false, ambiguous, printsUrl: false });
        setLinkDraft("");
        setLinkNewTab(false);
        return;
      }
      let attrs = {};
      try {
        attrs = (link.getAttributes && link.getAttributes()) || {};
      } catch (e) {
        attrs = {};
      }
      const href = attrs.href || "";
      setLinkState({ has: true, ambiguous: false, printsUrl: linkPrintsItsUrl(link, href) });
      setLinkDraft(href);
      setLinkNewTab((attrs.target || "") === "_blank");
    };

    editor.on("component:selected", (c) => {
      setRightTab("settings");
      setImageSelected(isImageComp(c));
      readLink(c);
    });
    editor.on("component:deselected", () => {
      setImageSelected(false);
      linkCompRef.current = null;
      clearTimeout(linkDoneTimerRef.current);
      setLinkSaved(false);
      setLinkState({ has: false, ambiguous: false, printsUrl: false });
      setLinkDraft("");
      setLinkNewTab(false);
    });

    // Seed once: prefer the editable builder design, else raw HTML.
    const hasDesign = design && typeof design === "object" && Object.keys(design).length > 0;
    try {
      if (hasDesign) {
        editor.loadProjectData(design);
      } else if (html) {
        // Reshaped first so the message is editable and its paragraphs are real
        // drop targets - see normaliseSeedHtml (Joshua Diokno, CRM-2026-00047).
        editor.setComponents(normaliseSeedHtml(html));
      }
    } catch (e) {
      // A malformed saved design shouldn't wipe the canvas silently.
      // eslint-disable-next-line no-console
      console.warn("EmailBuilder: could not seed design, starting blank", e);
    }

    // Give the email a proper centered 600px column with padding (like real
    // emails / Mailchimp) so content isn't flush against the edge on desktop.
    try {
      const wrapper = editor.getWrapper();
      if (wrapper) {
        wrapper.addStyle({
          "max-width": "600px",
          margin: "0 auto",
          padding: "32px 36px",
          background: "#ffffff",
          "box-sizing": "border-box",
        });
      }
    } catch (e) {
      /* noop */
    }

    // Produce the full email document + editable JSON on any change (debounced).
    // IMPORTANT: while a text block is being inline-edited (RTE active), we must
    // NOT run getHtml()/gjs-get-inlined-html - it force-syncs the text component
    // and resets the caret to the start (typing goes in backwards/garbled).
    // So we suppress emits during editing and flush once on rte:disable.
    let t = null;
    let rteActive = false;
    let destroyed = false;
    const runEmit = () => {
      // Never touch the editor once it's been torn down (unmount / dev
      // hot-reload leaves zombie listeners that would throw inside GrapesJS).
      if (destroyed || !editorRef.current) return;
      try {
        let inner = "";
        try {
          inner = editor.runCommand("gjs-get-inlined-html");
        } catch (e) {
          inner = `<style>${editor.getCss()}</style>${editor.getHtml()}`;
        }
        const html_body = wrapEmailDocument(inner, preheaderRef.current);
        let design_json = {};
        try {
          design_json = editor.getProjectData();
        } catch (e) {
          design_json = {};
        }
        onChangeRef.current && onChangeRef.current({ html_body, design_json });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("EmailBuilder: export skipped (editor unavailable)", e);
      }
    };
    const emit = () => {
      if (destroyed || rteActive) return; // don't touch the DOM mid-edit or after teardown
      clearTimeout(t);
      t = setTimeout(runEmit, 600);
    };
    // The link editor writes an attribute rather than moving a component, so it
    // asks for the export itself instead of waiting on a structural event.
    emitRef.current = runEmit;
    // Done tears the editor down, and with it anything not yet exported: words
    // still being typed (emits wait for the text box to close) and any change
    // inside the 600ms wait. The email then reverted to how it was before the
    // edit (CRM-2026-00129). So close the text box first, which writes the
    // typed words back into the design, and export straight away.
    flushRef.current = async () => {
      const editing = editor.getEditing();
      if (editing) {
        try {
          await editing.getView().disableEditing();
        } catch (e) {
          /* noop */
        }
      }
      clearTimeout(t); // closing the text box queued an emit of its own
      runEmit();
    };

    editor.on("rte:enable", () => {
      rteActive = true;
    });
    editor.on("rte:disable", () => {
      rteActive = false;
      emit(); // capture the finished text once editing ends
      // The other, more precise way to place a link: select the words you want
      // inside a paragraph and press the chain icon on the text toolbar. That
      // leaves an <a> with an empty address, which the agent then has to find.
      // Select it for them, so the "Where this button or link goes" box is
      // already open on the words they highlighted - the link goes exactly where
      // it was put, not wherever a dragged block landed.
      const edited = editor.getSelected();
      if (!edited) return;
      const blank = findBlankLink(edited);
      if (!blank) return;
      setTimeout(() => {
        if (destroyed) return;
        try {
          editor.select(blank);
        } catch (e) {
          /* noop */
        }
      }, 0);
    });

    editor.on("update", emit); // structural changes (add/move/remove/style)
    editor.on("component:add", emit);
    editor.on("component:remove", emit);
    editor.on("canvas:drop", emit);

    return () => {
      destroyed = true;
      clearTimeout(t);
      clearTimeout(linkTimerRef.current);
      clearTimeout(linkDoneTimerRef.current);
      emitRef.current = null;
      flushRef.current = null;
      linkCompRef.current = null;
      try {
        editor.off(); // drop all listeners before teardown
      } catch (e) {
        /* noop */
      }
      try {
        editor.destroy();
      } catch (e) {
        /* noop */
      }
      editorRef.current = null;
    };
    // Mount once. Re-seeding a different design is done by remounting via `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ed = () => editorRef.current;
  const done = async () => {
    if (flushRef.current) await flushRef.current();
    onClose();
  };
  const chooseDevice = (name) => {
    setDevice(name);
    try {
      ed()?.setDevice(name);
    } catch (e) {
      /* noop */
    }
  };
  const doUndo = () => {
    try {
      ed()?.runCommand("core:undo");
    } catch (e) {
      /* noop */
    }
  };
  const doRedo = () => {
    try {
      ed()?.runCommand("core:redo");
    } catch (e) {
      /* noop */
    }
  };
  const togglePreview = () => {
    const e = ed();
    if (!e) return;
    try {
      if (previewing) {
        e.stopCommand("preview");
        setPreviewing(false);
      } else {
        e.runCommand("preview");
        setPreviewing(true);
      }
    } catch (err) {
      /* noop */
    }
  };
  /**
   * Write the typed address onto the link behind the selection, then export at
   * once so the campaign holds the new destination even if the agent closes the
   * designer straight after (Paul Jimoulis, CRM-2026-00047).
   */
  const commitLink = (url, newTab) => {
    const link = linkCompRef.current;
    if (!link) return false;
    const next = (url || "").trim();
    try {
      link.addAttributes({ href: next });
      if (newTab) link.addAttributes({ target: "_blank", rel: "noopener noreferrer" });
      else if (link.removeAttributes) link.removeAttributes(["target", "rel"]);
      // The "Or paste this link into your browser" line prints the address as
      // its own words, so a new destination has to rewrite the words too or the
      // email shows one address and goes to another.
      if (linkState.printsUrl && next) link.components(next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("EmailBuilder: could not set the link", e);
      return false;
    }
    emitRef.current && emitRef.current();
    return true;
  };
  // Typing commits on a short pause as well as on Enter / blur / Apply, so a
  // pasted address is never lost to closing the designer too quickly.
  const onLinkTyped = (value) => {
    setLinkDraft(value);
    clearTimeout(linkTimerRef.current);
    linkTimerRef.current = setTimeout(() => commitLink(value, linkNewTab), 500);
  };
  const commitLinkNow = () => {
    clearTimeout(linkTimerRef.current);
    return commitLink(linkDraft, linkNewTab);
  };
  /** Drop the selection, which takes the link box off the Settings rail. */
  const closeLinkBox = () => {
    try {
      // No argument = deselect everything; the component:deselected listener
      // clears the box. State is reset here too so the box still closes if the
      // editor has already gone away underneath us.
      ed()?.select();
    } catch (e) {
      /* noop */
    }
    linkCompRef.current = null;
    setLinkState({ has: false, ambiguous: false, printsUrl: false });
    setLinkDraft("");
    setLinkNewTab(false);
  };
  /**
   * "Apply link" is a deliberate press, so unlike the quiet save-as-you-type it
   * answers back: the button turns into "Link saved", then the box closes itself as
   * the confirmation the work is in. Michael Piccoli, CRM-2026-00047 - pressing
   * Apply saved the address but told the agent nothing, so it looked broken.
   */
  const applyLinkAndClose = () => {
    if (!commitLinkNow()) return;
    setLinkSaved(true);
    clearTimeout(linkDoneTimerRef.current);
    linkDoneTimerRef.current = setTimeout(() => {
      setLinkSaved(false);
      closeLinkBox();
    }, 900);
  };
  const onLinkTabToggle = (checked) => {
    setLinkNewTab(checked);
    clearTimeout(linkTimerRef.current);
    commitLink(linkDraft, checked);
  };

  // Open the asset manager for the selected image and swap its source in place.
  // This is the same flow as double-clicking an image; exposed as an explicit
  // button so swapping a picture never requires deleting and re-adding it.
  const replaceImage = () => {
    const e = ed();
    if (!e) return;
    const target = e.getSelected();
    if (!target) return;
    const am = e.AssetManager;
    try {
      am.open({
        types: ["image"],
        accept: "image/*",
        // GrapesJS calls select(asset, complete): complete is false on a single
        // click, true on double-click / URL "Add". Swap the src on the selected
        // image and close once confirmed - no delete-and-re-add needed.
        select(asset, complete) {
          const src = asset && (asset.getSrc ? asset.getSrc() : asset.get && asset.get("src"));
          if (src) {
            try {
              target.addAttributes ? target.addAttributes({ src }) : target.set("src", src);
            } catch (err) {
              /* noop */
            }
          }
          if (complete) am.close();
        },
      });
    } catch (err) {
      // Fall back to the core command (double-click equivalent).
      try {
        e.runCommand("open-assets", { target });
      } catch (e2) {
        /* noop */
      }
    }
  };

  // Mailchimp-style light layout: top bar, blocks rail (left), canvas (centre),
  // style/settings/layers rail (right). Rendered in a portal so no parent
  // overflow/transform can clip it. Changes save live via onChange.
  return createPortal(
    <div className="ceb" data-preview={previewing ? "1" : "0"}>
      <header className="ceb-top">
        <div className="ceb-top__left">
          <Pencil className="ceb-top__logo" />
          <span className="ceb-top__title">{title}</span>
        </div>
        <div className="ceb-top__center">
          <button
            type="button"
            className={`ceb-dev ${device === "Desktop" ? "is-on" : ""}`}
            onClick={() => chooseDevice("Desktop")}
            title="Desktop"
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            type="button"
            className={`ceb-dev ${device === "Mobile" ? "is-on" : ""}`}
            onClick={() => chooseDevice("Mobile")}
            title="Mobile"
          >
            <Smartphone className="w-4 h-4" />
          </button>
        </div>
        <div className="ceb-top__right">
          <button type="button" className="ceb-icon" onClick={doUndo} title="Undo">
            <Undo2 className="w-4 h-4" />
          </button>
          <button type="button" className="ceb-icon" onClick={doRedo} title="Redo">
            <Redo2 className="w-4 h-4" />
          </button>
          <button type="button" className="ceb-ghost" onClick={togglePreview}>
            <Eye className="w-4 h-4" /> {previewing ? "Back to editing" : "Preview"}
          </button>
          <button type="button" className="ceb-done" onClick={done}>
            <X className="w-4 h-4" /> Done
          </button>
        </div>
      </header>

      <div className="ceb-main">
        <aside className="ceb-left">
          <div className="ceb-left__hd">Content blocks</div>
          <div className="ceb-left__hint">
            Drag a block into your email. <strong>Double-click any text to type.</strong> To put a
            link on words you have already written, highlight them and press the chain icon.
          </div>
          <div ref={blocksRef} className="ceb-blocks" />
        </aside>

        <main className="ceb-center">
          <div ref={canvasRef} className="ceb-canvas" />
        </main>

        <aside className="ceb-right">
          <div className="ceb-tabs">
            {[
              ["settings", "Settings"],
              ["style", "Style"],
              ["layers", "Layers"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`ceb-tab ${rightTab === k ? "is-on" : ""}`}
                onClick={() => setRightTab(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="ceb-right__body">
            <div className="ceb-pane" hidden={rightTab !== "settings"}>
              {linkState.has && (
                <div className="ceb-linktools">
                  <div className="ceb-linktools__hd">Where this button or link goes</div>
                  <input
                    type="text"
                    className="ceb-linkinput"
                    value={linkDraft}
                    placeholder="https://www.collings.com.au/listing/..."
                    onChange={(e) => onLinkTyped(e.target.value)}
                    onBlur={commitLinkNow}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        applyLinkAndClose();
                      }
                    }}
                  />
                  <label className="ceb-linkopt">
                    <input
                      type="checkbox"
                      checked={linkNewTab}
                      onChange={(e) => onLinkTabToggle(e.target.checked)}
                    />
                    Open in a new tab
                  </label>
                  <button
                    type="button"
                    className={`ceb-linkapply ${linkSaved ? "is-saved" : ""}`}
                    onClick={applyLinkAndClose}
                    disabled={linkSaved}
                  >
                    {linkSaved ? (
                      <>
                        <Check className="w-4 h-4" /> Link saved
                      </>
                    ) : (
                      "Apply link"
                    )}
                  </button>
                  <p className="ceb-imgtools__hint" role="status" aria-live="polite">
                    {linkSaved
                      ? "Saved. Closing this box."
                      : "Paste the page you want people taken to - the listing, the advertisement, a booking form. It saves as you type."}
                  </p>
                </div>
              )}
              {!linkState.has && linkState.ambiguous && (
                <p className="ceb-imgtools__hint ceb-linkhint">
                  There is more than one link in what you have selected. Click the button or the
                  link itself to set where it goes.
                </p>
              )}
              {imageSelected && (
                <div className="ceb-imgtools">
                  <button type="button" className="ceb-replace" onClick={replaceImage}>
                    <ImageIcon className="w-4 h-4" /> Replace image
                  </button>
                  <p className="ceb-imgtools__hint">
                    Swap this picture without deleting it - upload a new file or paste an image URL.
                    You can also just double-click the image in the canvas.
                  </p>
                </div>
              )}
              <div ref={selectorsRef} className="ceb-selectors" />
              <div ref={settingsRef} className="ceb-traits" />
            </div>
            <div className="ceb-pane" ref={styleRef} hidden={rightTab !== "style"} />
            <div className="ceb-pane" ref={layersRef} hidden={rightTab !== "layers"} />
          </div>
        </aside>
      </div>
    </div>,
    document.body
  );
}
